import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { StagedDestinationWarmer } from '@ui-rocco/pages/directory-browse/StagedDestinationWarmer';
import { useAtomValue, useSetAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { Button as RoccoButton } from '@rocco/components/button';
import {
    Filters,
    FiltersProvider,
    type FilterSortOption,
} from '@rocco/ui/filter-sort/components/Filters';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { BackButton } from '../products-page/components/BackButton';
import { useDragScroll } from '../category-page/hooks/useDragScroll';
import { useScrollFade } from '../category-page/hooks/useScrollFade';
import { SortDropdown } from '@rocco/ui/filter-sort/components/SortDropdown';
import { AppliedFiltersRow } from '@rocco/ui/filter-sort/components/AppliedFiltersRow';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import {
    FiltersModal,
    getActiveFiltersCount,
} from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { uiStateAtomFiltersModalIsOpened } from '@rocco/ui/filter-sort/states/ui-states-filters-modal';
import { CategoryPills } from '~/ui-rocco/pages/category-page/components/CategoryPills';
import {
    BLEED_BREAKOUT_RIGHT,
    BLEED_GUTTER_X,
} from '~/ui-rocco/pages/detail-shared';
import { useDirectoryBrowseTheme } from './directory-browse-theme';
import { useDirectoryFilterDrawer } from './directory-filter-drawer';
import { DirectoryDisplayDropdown } from './DirectoryDisplayDropdown';
import type { DirectoryGridColumns } from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';

export interface DirectoryPillBarBreadcrumb {
    href: string;
    label: string;
}

/**
 * Text prompt rendered beside the section heading offering the other browse
 * mode, e.g. "Prefer to browse by Brands?" on the products grid. Used by the
 * revamped browse page in place of {@link DirectoryPillBarToggle}: it reads as
 * an invitation rather than an unlabelled switch, which is unclear to anyone
 * who does not already know the platform.
 */
export interface DirectoryPillBarEntitySwitch {
    /**
     * The browse modes in a fixed order. Order must not depend on which one is
     * active: the refine sheet renders them as pills, and reordering on switch
     * makes them jump under the user's finger.
     *
     * The desktop prompt names whichever mode is not active; the mobile sheet
     * shows all of them.
     */
    modes: {
        label: string;
        isActive: boolean;
        onSelect: () => void;
    }[];
}

/**
 * Optional entity toggle rendered on the right of the bar (e.g. products'
 * Products/Brands switch). Entities without a second mode (professionals,
 * projects) omit this and the toggle UI is not rendered.
 */
export interface DirectoryPillBarToggle {
    leftLabel: string;
    rightLabel: string;
    /** Whether the right-hand mode is active (e.g. suppliers/brands). */
    isRightActive: boolean;
    onToggle: () => void;
    localRightActive: boolean;
    disableHighlightTransition: boolean;
    setLocalRightActive: (value: boolean) => void;
    setDisableHighlightTransition: (value: boolean) => void;
    /** When the right mode is active, show these category pills instead of filter pills. */
    rightCategories?: { id: number; title: string; link: string }[];
}

export interface DirectoryPillBarProps {
    breadcrumbs: DirectoryPillBarBreadcrumb[];
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    /** Optional entity toggle. The legacy products bar passes it; the revamp uses entitySwitch. */
    toggle?: DirectoryPillBarToggle;
    /** Optional text prompt beside the heading offering the other browse mode. */
    entitySwitch?: DirectoryPillBarEntitySwitch;
    /**
     * Overrides the noun in the refine sheet's apply CTA. Needed where the page
     * can switch what it lists, so the CTA does not keep saying "Products"
     * while counting brands.
     */
    resultName?: string;
    /**
     * Heading shown above the grid (the active category). Defaults to the
     * deepest breadcrumb, or "All Products" at the directory root.
     */
    sectionTitle?: string;
    /**
     * Child-category pills shown as the primary filter row (the 5.0 design uses
     * category pills, not facet dropdowns). Should include a leading "All …"
     * entry linking to the directory root. When omitted the bar falls back to
     * the facet filter dropdowns.
     */
    categoryPills?: { id: number; title: string; link: string }[];
    /** Directory root path — used to label the back button (default /products). */
    rootPath?: string;
    /** Directory root label for the back button (default "All Products"). */
    rootLabel?: string;
    /**
     * True when categoryPills carry the SIBLING set (leaf categories have no
     * children) — rendered below md only, where switching across siblings
     * matters; desktop keeps its childless presentation.
     */
    pillsScopedToMobile?: boolean;
    /**
     * The URL the sheet's staged filters would commit to (from the settled
     * aggregate). While the sheet is open, its route data is prefetched so
     * the Apply navigation serves from the browser cache.
     */
    prefetchUrl?: string | null;
    /**
     * Current desktop column count for the "Display: {n}" dropdown beside
     * Sort. Provide together with onDisplayColumnsChange; omitted on pages
     * without the control (the legacy products browse).
     */
    displayColumns?: DirectoryGridColumns;
    /** Applies a new Display column count. */
    onDisplayColumnsChange?: (columns: DirectoryGridColumns) => void;
}

const FilterPillIcon = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
        className="shrink-0"
    >
        <path
            d="M1.5 3.75h12M3.5 7.5h8M5.5 11.25h4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
        />
    </svg>
);

/** Shared visual style for both the desktop and mobile "Show Filters" pill.
 * `max-md:min-h-[44px]` guarantees a touch-friendly hit area below md even
 * though the visual pill height (h-10.5) sits a little under 44px at small
 * viewports; the row's `overflow-visible` already tolerates the pill
 * overflowing its row slightly. */
const filterPillClassName = (isDark: boolean) =>
    cn(
        'flex h-10.5 shrink-0 items-center gap-2 rounded-full px-[1.25rem] text-[1rem] tracking-[-0.05rem] transition-colors max-md:min-h-[44px]',
        isDark
            ? 'bg-[#d5d5d5] text-[#181818] hover:bg-[#c8c8c8] active:bg-[#bcbcbc]'
            : 'bg-[#181818] text-white hover:bg-[#2a2a2a] active:bg-[#333333]',
    );

/**
 * Applied-facet count badge for the "Show Filters" pill — once the sidebar
 * (desktop) or bottom sheet (mobile) is closed, this is the only visible
 * indicator that filters are active.
 */
const FilterPillCount = ({
    count,
    isDark,
}: {
    count: number;
    isDark: boolean;
}) => {
    if (count <= 0) return null;
    return (
        <span
            className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-medium',
                isDark ? 'bg-[#181818] text-white' : 'bg-white text-[#181818]',
            )}
        >
            {count}
        </span>
    );
};

/**
 * "Show Filters" / "Hide Filters" pill (md and above) — toggles the left
 * facet sidebar. Renders nothing when the page has no sidebar.
 */
const FilterToggleButtonDesktop = ({
    isDark,
    activeFiltersCount,
}: {
    isDark: boolean;
    activeFiltersCount: number;
}) => {
    const { open, toggle, hasSidebar } = useDirectoryFilterDrawer();
    if (!hasSidebar) return null;
    return (
        <button
            type="button"
            onClick={toggle}
            aria-pressed={open}
            // The breakpoint gate must be modifier-scoped (max-md:hidden), not a
            // bare `hidden`: the base pill style starts with `flex`, and
            // tailwind-merge treats an unscoped hidden/flex pair as one display
            // conflict and keeps only the later class — silently un-hiding this
            // pill on mobile.
            className={cn(filterPillClassName(isDark), 'max-md:hidden')}
        >
            <FilterPillIcon />
            {open ? 'Hide Filters' : 'Show Filters'}
            <FilterPillCount count={activeFiltersCount} isDark={isDark} />
        </button>
    );
};

/**
 * "Refine" pill (below md) — the persistent sidebar has nowhere to live on
 * mobile (CollapsiblePageLayout hides it there), so this opens the shared
 * FiltersModal bottom sheet (which also carries Sort By on mobile) instead. It
 * is styled as a peer of the child-category pills rather than the heavier
 * desktop toggle, so the row reads as a single set of pills.
 */
const FilterToggleButtonMobile = ({
    isDark,
    activeFiltersCount,
}: {
    isDark: boolean;
    activeFiltersCount: number;
}) => {
    const { hasSidebar } = useDirectoryFilterDrawer();
    const isSheetOpen = useAtomValue(uiStateAtomFiltersModalIsOpened);
    const setSheetOpen = useSetAtom(uiStateAtomFiltersModalIsOpened);
    if (!hasSidebar) return null;
    return (
        <button
            type="button"
            onClick={() => setSheetOpen((prev) => !prev)}
            aria-pressed={isSheetOpen}
            aria-label="Refine"
            className={cn(
                // Figma 2354:63263 — icon-only charcoal circle (inverts on the
                // dark editorial theme). The count badge overlays the corner
                // so applied-filter state stays visible without a label.
                'relative flex size-11 shrink-0 items-center justify-center rounded-full md:hidden',
                isDark ? 'bg-white text-[#181818]' : 'bg-[#181818] text-white',
            )}
        >
            <FilterPillIcon />
            {activeFiltersCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5">
                    <FilterPillCount
                        count={activeFiltersCount}
                        isDark={isDark}
                    />
                </span>
            ) : null}
        </button>
    );
};

/** Matches Tailwind's `md` breakpoint. Used below to resolve the real
 * DisplayMode for the one child that genuinely behaves differently by
 * viewport (the "All Filters" modal — bottom sheet + staged Apply on mobile
 * vs. left panel + immediate apply on desktop), instead of forcing Desktop
 * regardless of screen size. SSR-safe: starts false and resolves on mount. */
const BELOW_MD_QUERY = '(max-width: 767.98px)';

const useIsBelowMd = (): boolean => {
    const [belowMd, setBelowMd] = useState(false);
    useEffect(() => {
        const query = window.matchMedia(BELOW_MD_QUERY);
        setBelowMd(query.matches);
        const onChange = () => setBelowMd(query.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    return belowMd;
};

/**
 * Shared desktop filter/pill bar for the directory browse pages (products,
 * professionals, projects). Back button + scrollable filter pills + Sort, with
 * an optional entity toggle. Generalised from the products-only filter bar so
 * all three entities render the same bar off their own filter config.
 */
export const DirectoryPillBar = ({
    breadcrumbs,
    searchFilters,
    sortOptions,
    toggle,
    entitySwitch,
    resultName,
    sectionTitle,
    categoryPills,
    rootPath = '/products',
    rootLabel = 'All Products',
    pillsScopedToMobile = false,
    prefetchUrl = null,
    displayColumns,
    onDisplayColumnsChange,
}: DirectoryPillBarProps) => {
    const { scrollRef } = useDragScroll();
    const location = useLocation();
    const isSheetOpen = useAtomValue(uiStateAtomFiltersModalIsOpened);
    const setSheetOpen = useSetAtom(uiStateAtomFiltersModalIsOpened);
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const isBelowMd = useIsBelowMd();
    const { hasSidebar } = useDirectoryFilterDrawer();
    // Applied-facet count for the "Show Filters" pill badge, and to gate
    // mounting the mobile FiltersModal bottom sheet below.
    const activeFiltersCount = getActiveFiltersCount(
        searchFilters.filterStates,
    );

    // Active-category heading. Falls back to the deepest breadcrumb, or the
    // directory root label when there is no sub-category selected.
    const sectionHeading =
        sectionTitle ??
        (breadcrumbs.length > 1
            ? breadcrumbs[breadcrumbs.length - 1]?.label
            : 'All Products') ??
        'All Products';

    // Track scroll position for dynamic fade masks / grab cursor
    const { showLeftFade, showRightFade, isOverflowing } = useScrollFade(
        scrollRef as React.RefObject<HTMLDivElement | null>,
    );

    // Extract parent category info from breadcrumbs (second-to-last is the parent)
    const parentBreadcrumb =
        breadcrumbs?.length >= 2 ? breadcrumbs[breadcrumbs.length - 2] : null;
    const parentCategoryName = parentBreadcrumb?.label;
    const parentCategoryLink = parentBreadcrumb?.href;
    const shouldShowBackButton = !!parentCategoryLink && !!parentCategoryName;

    // The desktop prompt offers whichever mode is not active. The sheet renders
    // every mode, so it keeps the full ordered list.
    const offeredMode = entitySwitch?.modes.find((mode) => !mode.isActive);

    const showRightCategoryPills =
        !!toggle?.isRightActive &&
        !!toggle.rightCategories &&
        toggle.rightCategories.length > 0;

    return (
        <>
            {/* Row 1: Filter bar - fixed height prevents layout shift, overflow-visible for badges.
                `max-md:min-h-[44px]` floors the row below md so its pill children
                (which need a ≥44px tap target there) aren't squeezed into the
                slimmer 42px desktop visual — overflow-visible already tolerates
                the row's content overflowing that box on the 2px difference. */}
            {/* Mobile vertical rhythm lives on the sticky pill-bar section
                (DirectoryBrowsePage) so the bar's background wraps it when
                stuck to the header. */}
            <div
                className={cn(
                    'flex h-10.5 items-center gap-3 overflow-visible px-4 max-md:min-h-[44px] qhd:overflow-x-clip',
                    // md+ the row's gutters track the centred 1920 container
                    // (48px until the cap binds) while the pill scrollers
                    // below break back out to the viewport edge.
                    BLEED_GUTTER_X,
                )}
            >
                {/* Back Button - only show when not at root. In the category-pill
                    layout it moves into the category group (after Show Filters);
                    here it leads the other layouts. */}
                {!categoryPills &&
                    shouldShowBackButton &&
                    parentCategoryName && (
                        <BackButton
                            parentCategoryName={parentCategoryName}
                            parentCategoryLink={parentCategoryLink}
                        />
                    )}

                {/* Filter Pills or Category Pills - scrollable with fade masks */}
                {showRightCategoryPills && toggle?.rightCategories ? (
                    // Full-bleed to the row's right edge, matching the
                    // child-category pills branch below. `max-md:[&_a]:min-h`
                    // floors each pill link's tap target at 44px below md —
                    // CategoryPills is shared elsewhere so this can't be set
                    // on its own h-10.5 (42px) pills directly.
                    <div
                        className={cn(
                            '-mr-4 flex min-w-0 flex-1 max-md:[&_a]:min-h-[44px]',
                            BLEED_BREAKOUT_RIGHT,
                        )}
                    >
                        <CategoryPills categories={toggle.rightCategories} />
                    </div>
                ) : categoryPills ? (
                    // 5.0 design: a "Show Filters" pill (opens the facet modal) |
                    // separator | back button | the child-category pills. The
                    // facet dropdowns move into the modal, so this row is category
                    // navigation. The pills scroll horizontally (hidden scrollbar)
                    // rather than pushing the page wide, so everything else is
                    // shrink-0 and the pills take the remaining width (min-w-0).
                    <>
                        <div className="flex shrink-0">
                            <FilterToggleButtonDesktop
                                isDark={isDark}
                                activeFiltersCount={activeFiltersCount}
                            />
                            <FilterToggleButtonMobile
                                isDark={isDark}
                                activeFiltersCount={activeFiltersCount}
                            />
                        </div>
                        {/* Mobile divider between the refine circle and the
                            scrolling pill set (desktop has its own further in). */}
                        <span
                            aria-hidden="true"
                            className={cn(
                                'h-[2.1rem] w-px shrink-0 md:hidden',
                                isDark ? 'bg-white/20' : 'bg-[#232323]/20',
                            )}
                        />
                        {hasSidebar &&
                            searchFilters.config.modalFilters.length > 0 && (
                                // Below md there is no persistent sidebar to toggle
                                // (CollapsiblePageLayout hides it there) — mount the
                                // shared FiltersModal bottom sheet here, driven by
                                // the mobile pill above via the shared open-state
                                // atom. Its own internal trigger button is unused
                                // (our pill drives `open`), so it's kept out of the
                                // visual layout; the Sheet content itself still
                                // portals and displays normally when opened.
                                // `showSorting` surfaces sort inside the sheet
                                // (mobile displayMode only), since row 2's desktop
                                // Sort dropdown is hidden below md. The refine
                                // presentation stages sort with the other
                                // controls and commits everything through the
                                // sheet's CTA; the staged category tree drills
                                // through the vertical's category hierarchy
                                // without leaving the sheet.
                                <FiltersProvider
                                    searchFilters={searchFilters}
                                    sortOptions={sortOptions}
                                    displayMode={DisplayMode.Mobile}
                                >
                                    <div className="hidden">
                                        <FiltersModal
                                            showSorting={true}
                                            presentation="refine"
                                            categoryTree={{ rootLabel }}
                                            // Mobile counterpart of the
                                            // "Prefer to browse by …" prompt,
                                            // which is desktop-only.
                                            browseMode={
                                                entitySwitch && {
                                                    // Switching navigates, so
                                                    // dismiss the sheet rather
                                                    // than leaving it covering
                                                    // the new mode's grid.
                                                    options:
                                                        entitySwitch.modes.map(
                                                            (mode) => ({
                                                                ...mode,
                                                                onSelect:
                                                                    () => {
                                                                        setSheetOpen(
                                                                            false,
                                                                        );
                                                                        mode.onSelect();
                                                                    },
                                                            }),
                                                        ),
                                                }
                                            }
                                            resultName={resultName}
                                        />
                                    </div>
                                </FiltersProvider>
                            )}
                        {/* Warm the staged destination while the sheet is
                            open: by the time the user taps Apply, the route
                            data is already in the browser's HTTP cache
                            (entry.server gives prefetch-purposed data
                            requests a short max-age). PrefetchPageLinks
                            computes the single-fetch URL; the wrapper span
                            lets StagedDestinationWarmer find the rendered
                            link and back it with a fetch — Safari has no
                            link-prefetch support at all, and memory-only
                            caches (incognito) evict link entries under
                            image churn, so the link alone is not reliable. */}
                        {isSheetOpen &&
                            prefetchUrl &&
                            prefetchUrl !==
                                location.pathname + location.search && (
                                <StagedDestinationWarmer page={prefetchUrl} />
                            )}
                        {/* Separator + parent back pill are desktop chrome. Below
                            md they crowd the row and the Refine pill instead sits
                            directly among the category pills, so hide the whole
                            group there — mobile users navigate back via the pills
                            or the browser. Base `flex` + scoped `max-md:hidden`
                            keep desktop spacing byte-for-byte (the row's gap-3 now
                            lands on this wrapper) while collapsing it below md. */}
                        <div className="flex shrink-0 items-center gap-3 max-md:hidden">
                            <span
                                aria-hidden="true"
                                className={cn(
                                    'h-[2.1rem] w-px shrink-0',
                                    isDark ? 'bg-white/20' : 'bg-[#232323]/20',
                                )}
                            />
                            {shouldShowBackButton && parentCategoryName && (
                                <BackButton
                                    // A level-1 category's parent is the root listing:
                                    // label it "All {Entity}" rather than the root
                                    // breadcrumb's bare name.
                                    parentCategoryName={
                                        parentCategoryLink === rootPath
                                            ? rootLabel
                                            : parentCategoryName
                                    }
                                    parentCategoryLink={parentCategoryLink}
                                    variant={isDark ? 'light' : 'dark'}
                                />
                            )}
                        </div>
                        {/* Full-bleed: cancel the row's right padding so the pills
                            scroll all the way to the container edge on overflow.
                            `max-md:[&_a]:min-h` floors each pill link's tap
                            target at 44px below md — CategoryPills is shared
                            elsewhere so this can't be set on its own h-10.5
                            (42px) pills directly. */}
                        <div
                            className={cn(
                                '-mr-4 -ml-3 flex min-w-0 flex-1 max-md:[&_a]:min-h-[44px]',
                                BLEED_BREAKOUT_RIGHT,
                                pillsScopedToMobile && 'md:hidden',
                            )}
                        >
                            <CategoryPills
                                categories={categoryPills}
                                theme={isDark ? 'dark' : 'light'}
                                // Mobile keeps the parent back pill but lets it
                                // scroll away with the set — desktop pins it
                                // outside the scroller instead.
                                leading={
                                    shouldShowBackButton &&
                                    parentCategoryName ? (
                                        <BackButton
                                            parentCategoryName={
                                                parentCategoryLink === rootPath
                                                    ? rootLabel
                                                    : parentCategoryName
                                            }
                                            parentCategoryLink={
                                                parentCategoryLink
                                            }
                                            variant={isDark ? 'light' : 'dark'}
                                        />
                                    ) : undefined
                                }
                            />
                        </div>
                    </>
                ) : (
                    // Full-bleed on the right edge too, matching the
                    // category-pill branches above (and the category-carousel
                    // convention: left padding stays, right padding is
                    // cancelled so the row scrolls flush to the edge).
                    <div
                        className={cn(
                            '-mr-4 min-w-0 flex-1 overflow-visible',
                            BLEED_BREAKOUT_RIGHT,
                        )}
                    >
                        <div className="relative overflow-visible">
                            {/* Left fade overlay - only visible when scrolled */}
                            {showLeftFade && (
                                <div
                                    className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6"
                                    style={{
                                        background:
                                            'linear-gradient(to right, white, transparent)',
                                    }}
                                />
                            )}
                            {/* Right fade overlay - only visible when more content
                                available. Hidden below md (`max-md:hidden`) so the
                                pill scroller matches the mobile carousels' full-bleed
                                hard right edge; kept at md+. */}
                            {showRightFade && (
                                <div
                                    className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 max-md:hidden"
                                    style={{
                                        background:
                                            'linear-gradient(to left, white, transparent)',
                                    }}
                                />
                            )}
                            {/* Scroll container - badge space (-my-4 py-4) is INSIDE to prevent clipping.
                                Only show the grab cursor when pills actually overflow; otherwise the
                                flex-1 strip makes the empty space look draggable. */}
                            <div
                                ref={
                                    scrollRef as React.RefObject<HTMLDivElement | null>
                                }
                                className={cn(
                                    '-my-4 scrollbar-hide flex items-center gap-3 overflow-x-auto py-4',
                                    isOverflowing &&
                                        'md:cursor-grab md:active:cursor-grabbing',
                                )}
                            >
                                {searchFilters && (
                                    <Filters
                                        searchFilters={searchFilters}
                                        sortOptions={sortOptions}
                                        // Pinned to Desktop so the static bar
                                        // filter pills below still render
                                        // below md — FiltersBar (which they
                                        // and barTrailingComponent live in)
                                        // only renders when this resolves to
                                        // Desktop, and the mobile pill bar is
                                        // deliberately the same horizontal
                                        // scroller, not a different layout.
                                        // The "All Filters" trigger below is
                                        // the one piece that genuinely
                                        // differs by viewport (bottom sheet +
                                        // staged Apply on mobile vs. left
                                        // panel + immediate apply on desktop),
                                        // so it gets the real, client-resolved
                                        // mode via its own provider instead of
                                        // inheriting this Desktop pin.
                                        displayMode={DisplayMode.Desktop}
                                        showSortBy={false}
                                        hideModalButton={true}
                                        showSelectedCountBadge={true}
                                        disableWrap={true}
                                        filterPillClassName="border-[#F2F2F2] bg-[#F2F2F2] text-[#323232] hover:border-gray-200 hover:bg-gray-200 active:border-gray-300 active:bg-gray-300 px-5 py-2.5 tracking-tighter max-md:min-h-[44px] [&>svg]:size-3.5 [&>svg]:ml-2.5"
                                        scrollableClassName="gap-3"
                                        filterButtonClassName="px-5 py-2.5 tracking-tighter [&>svg]:size-4 [&>svg]:ml-2.5"
                                        barTrailingComponent={
                                            toggle?.isRightActive ? undefined : (
                                                <FiltersProvider
                                                    searchFilters={
                                                        searchFilters
                                                    }
                                                    sortOptions={sortOptions}
                                                    displayMode={
                                                        isBelowMd
                                                            ? DisplayMode.Mobile
                                                            : DisplayMode.Desktop
                                                    }
                                                    filterButtonClassName="px-5 py-2.5 tracking-tighter [&>svg]:size-4 [&>svg]:ml-2.5"
                                                >
                                                    <FiltersModal
                                                        showSorting={false}
                                                    />
                                                </FiltersProvider>
                                            )
                                        }
                                        hideFacetFilters={true}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Entity toggle (optional) - e.g. Products/Brands */}
                {toggle && (
                    <div className="ml-auto flex shrink-0 items-center gap-3">
                        <div className="relative flex items-center gap-2 rounded-full bg-[#ECECEC] px-[0.45rem] py-[0.3rem]">
                            {/* Sliding highlight background */}
                            <div
                                aria-hidden
                                className={cn(
                                    'absolute top-[0.3rem] left-[0.45rem] h-[calc(100%-0.6rem)] rounded-full bg-white shadow-md duration-300',
                                    toggle.disableHighlightTransition
                                        ? ''
                                        : 'transition-transform',
                                    toggle.localRightActive
                                        ? 'translate-x-full'
                                        : 'translate-x-0',
                                )}
                                style={{
                                    width: 'calc(50% - 0.45rem)',
                                }}
                            />

                            <RoccoButton
                                weight="normal"
                                clickAnimation="scale"
                                onClick={() => {
                                    if (toggle.isRightActive) {
                                        toggle.setDisableHighlightTransition(
                                            true,
                                        );
                                        toggle.setLocalRightActive(false);
                                        requestAnimationFrame(() => {
                                            setTimeout(() => {
                                                toggle.onToggle();
                                                toggle.setDisableHighlightTransition(
                                                    false,
                                                );
                                            }, 50);
                                        });
                                    }
                                }}
                                className={cn(
                                    'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                    !toggle.isRightActive
                                        ? 'font-medium text-[#232323]'
                                        : 'font-normal text-[#232323] opacity-60',
                                )}
                            >
                                <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                    {toggle.leftLabel}
                                </span>
                            </RoccoButton>

                            <RoccoButton
                                weight="normal"
                                clickAnimation="scale"
                                onClick={() => {
                                    if (!toggle.isRightActive) {
                                        toggle.setDisableHighlightTransition(
                                            true,
                                        );
                                        toggle.setLocalRightActive(true);
                                        requestAnimationFrame(() => {
                                            setTimeout(() => {
                                                toggle.onToggle();
                                                toggle.setDisableHighlightTransition(
                                                    false,
                                                );
                                            }, 50);
                                        });
                                    }
                                }}
                                className={cn(
                                    'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                    toggle.isRightActive
                                        ? 'font-medium text-[#232323]'
                                        : 'font-normal text-[#232323] opacity-60',
                                )}
                            >
                                <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                    {toggle.rightLabel}
                                </span>
                            </RoccoButton>
                        </div>
                    </div>
                )}
            </div>

            {/* Row 2: Section title (active category) + Sort By.
                The visible breadcrumb row is replaced by the category heading
                per the 5.0 design; breadcrumb data still feeds the page's
                JSON-LD (SEO) elsewhere. Hidden below md (`max-md:hidden`): the
                heading is desktop-only and Sort By moves into the mobile filter
                sheet, so the whole row collapses to reclaim mobile real estate. */}
            <div className={cn('px-4 pt-12 max-md:hidden', BLEED_GUTTER_X)}>
                <hr
                    className={cn(
                        'm-0 h-0 border-t opacity-20',
                        isDark ? 'border-white' : 'border-[#232323]',
                    )}
                />
                <div className="flex items-center justify-between gap-4 pt-5 pb-6">
                    {/* Heading and the browse-mode prompt travel together on the
                        left so Sort stays pinned right. */}
                    <div className="flex min-w-0 items-baseline gap-4">
                        <h2
                            className={cn(
                                // Scaled down below md (matching the "Explore
                                // Categories" heading convention above this bar)
                                // and truncated so a long category name can't
                                // collide with Sort on a narrow viewport — it
                                // never needs to wrap onto a second line.
                                'm-0 min-w-0 truncate text-[1.75rem] leading-[2.25rem] font-normal tracking-[-0.02rem] md:text-[2rem] md:leading-[2.5rem]',
                                isDark ? 'text-white' : 'text-[#1f1f21]',
                            )}
                        >
                            ({sectionHeading})
                        </h2>
                        {offeredMode && (
                            <p
                                className={cn(
                                    // Sits next to the heading and gives way first:
                                    // the heading truncates only after this is
                                    // hidden, and Sort keeps its natural width.
                                    'm-0 hidden shrink-0 text-[1rem] leading-[1.5rem] font-normal md:block',
                                    isDark ? 'text-white/70' : 'text-[#6b6b6f]',
                                )}
                            >
                                Prefer to browse by{' '}
                                <button
                                    type="button"
                                    onClick={offeredMode.onSelect}
                                    className={cn(
                                        'cursor-pointer bg-transparent p-0 underline underline-offset-4',
                                        isDark
                                            ? 'text-white hover:text-white/80'
                                            : 'text-[#1f1f21] hover:text-[#6b6b6f]',
                                    )}
                                >
                                    {offeredMode.label}
                                </button>
                                ?
                            </p>
                        )}
                    </div>
                    {/* The arbitrary variant restyles only the trigger labels +
                        chevrons to the 22px / 16px design (options are portaled).
                        `shrink-0` keeps the controls at their natural width so
                        the heading truncates first, and `max-md:min-h-[44px]`
                        floors the trigger tap targets below md. */}
                    <div
                        className={cn(
                            'shrink-0 max-md:[&_button]:min-h-[44px] [&_button_svg]:size-4 [&_button>span]:text-[1.375rem] [&_button>span]:font-normal [&_button>span]:tracking-normal',
                            // Only lay out as a flex pair when the Display
                            // control is present — the legacy products browse
                            // renders this bar with Sort alone and keeps its
                            // original block layout.
                            displayColumns !== undefined &&
                                onDisplayColumnsChange &&
                                'flex items-center gap-8',
                            // The chevron svg has no fill attribute (defaults to
                            // black) and doesn't use currentColor, so it needs an
                            // explicit fill — text colour only flips the label.
                            isDark
                                ? '[&_button_svg]:fill-white [&_button>span]:text-white'
                                : '[&_button_svg]:fill-[#181818] [&_button>span]:text-[#181818]',
                        )}
                    >
                        {displayColumns !== undefined &&
                            onDisplayColumnsChange && (
                                <DirectoryDisplayDropdown
                                    columns={displayColumns}
                                    onColumnsChange={onDisplayColumnsChange}
                                />
                            )}
                        <SortDropdown
                            label="Sort by"
                            sortOptions={sortOptions}
                            updateSort={searchFilters.updateSort}
                        />
                    </div>
                </div>
                {/* Applied-filter pills above the grid. Desktop-only via the
                    row's max-md:hidden: below md the Refine sheet's chips row
                    carries the applied state. */}
                <AppliedFiltersRow
                    searchFilters={searchFilters}
                    theme={isDark ? 'dark' : 'light'}
                    className="pb-6"
                />
            </div>
        </>
    );
};
